import { useCallback, useEffect, useState } from 'react'

export type ActiveOption = {
  /** Index into the option list, or -1 when nothing is active. */
  activeIndex: number
  setActiveIndex: (index: number) => void
  /** Moves the active option, wrapping at both ends. */
  moveActiveIndex: (delta: number) => void
}

/**
 * Keyboard cursor for a dropdown list.
 *
 * Both dropdowns behind the search field — recent searches and typeahead suggestions — need the
 * same wrap-around walk and the same rules about when the cursor goes stale. Two copies of that
 * would be two chances for the field to behave differently depending on what it happens to be
 * showing, so it lives in one place.
 *
 * @param length - How many options are currently rendered
 * @param resetKey - Changes whenever the list stands for something new, clearing the cursor
 */
export function useActiveOption(length: number, resetKey: unknown): ActiveOption {
  const [activeIndex, setActiveIndex] = useState(-1)

  // A cursor belongs to the list it was set against: `length` catches a list that changed size
  // under it, `resetKey` catches one that means something new at the same size.
  useEffect(() => {
    setActiveIndex(-1)
  }, [length, resetKey])

  const moveActiveIndex = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        if (length === 0) return -1

        // From "nothing active", Down lands on the first option and Up on the last.
        const next = current === -1 && delta < 0 ? length - 1 : current + delta

        return (next + length) % length
      })
    },
    [length]
  )

  return { activeIndex, setActiveIndex, moveActiveIndex }
}
